import { useTranslations } from "@/components/context/translations";
import { Fragment, useSyncExternalStore } from "react";

type FormReviewTopicsProps = {
  id: string;
  name: string;
  topics: string[];
  matchedIndexes?: number[];
};

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeReducedMotion(callback: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) {
    return () => {};
  }
  const mql = window.matchMedia(REDUCED_MOTION_QUERY);
  mql.addEventListener("change", callback);
  return () => mql.removeEventListener("change", callback);
}

function getReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

function useReducedMotion(): boolean {
  return useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotion,
    () => false,
  );
}

export function FormReviewTopics({ ...props }: FormReviewTopicsProps) {
  const t = useTranslations();
  const reducedMotion = useReducedMotion();
  const label = t("Suggested topics");
  const resolvedTopics = props.topics ?? [];
  const matchedIndexes = new Set(props.matchedIndexes ?? []);

  const topics = resolvedTopics.map((topic) => t(topic));
  if (topics.length === 0) return null;

  return (
    <p
      className="text-sm"
      style={{
        fontFamily: "var(--input-font-name)",
        fontStyle: "var(--input-font-style)",
        fontWeight: "var(--input-font-weight)",
        color: "var(--label-color)",
      }}
    >
      <span
        style={{ fontWeight: "bold", marginRight: "0.4em" }}
        dangerouslySetInnerHTML={{ __html: `${label}:` }}
      />
      {topics.map((topic, i) => {
        const matched = matchedIndexes.has(i);
        const tickTransition = reducedMotion
          ? "none"
          : "transform 150ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 150ms ease";
        return (
          <Fragment key={i}>
            <span
              id={`chip-${i}`}
              style={{
                opacity: matched ? 0.3 : 1,
                transition: reducedMotion ? "none" : "opacity 200ms ease",
              }}
            >
              {i > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    verticalAlign: "middle",
                    width: "0.6em",
                    margin: "0 0.35em",
                  }}
                >
                  <span
                    style={{
                      opacity: matched ? 0 : 1,
                      transition: reducedMotion ? "none" : "opacity 150ms ease",
                      position: matched ? "absolute" : "static",
                    }}
                  >
                    {"\u00B7"}
                  </span>
                  <svg
                    data-testid={`chip-indicator-${i}`}
                    width="1.15em"
                    height="1.15em"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      display: "inline-block",
                      transformOrigin: "center",
                      transform: matched ? "scale(1)" : "scale(0)",
                      opacity: matched ? 1 : 0,
                      transition: tickTransition,
                      verticalAlign: "middle",
                    }}
                  >
                    <path
                      d="M3 8.25L6.5 11.75L13 5.25"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
              {i === 0 && matched && (
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    verticalAlign: "middle",
                    width: "0.6em",
                    marginRight: "0.2em",
                  }}
                >
                  <svg
                    data-testid={`chip-indicator-${i}`}
                    width="1.15em"
                    height="1.15em"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      display: "inline-block",
                      transformOrigin: "center",
                      transform: "scale(1)",
                      transition: tickTransition,
                      verticalAlign: "middle",
                    }}
                  >
                    <path
                      d="M3 8.25L6.5 11.75L13 5.25"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
              {topic}
            </span>
          </Fragment>
        );
      })}
    </p>
  );
}
